import { Box, Menu, MenuItem, Tooltip, useTheme } from "@mui/material";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { alpha } from "@mui/material/styles";
import { isTruthy } from "helpers/methods";
import { FileField, RequestFile, RequestParticipant } from "models/interfaces";
import { useEffect, useRef, useState } from "react";
import { fieldColorFor } from "screens/SignRequest/SignRequestHelper";
import { menu } from "theme/colors";
import {
  customFieldValueError,
  emitFieldCommit,
} from "../ParticipantScreenService";

/**
 * Signer-side dropdown custom field. The trigger keeps the field's document
 * styling; the option list opens in the app's themed `Menu` (the same card as
 * the kebab menus) instead of the browser's native <select> popup. The value
 * stored is the option's text, which is what the server checks against
 * `listItems` and what the export shows.
 */
const CustomDropDownFieldElement = ({
  field,
  dispatch,
  selectedFile,
  currentReviewer,
  currentFieldOnFocus,
  setCurrentFieldOnFocus,
  moveToFieldLocation,
  signingJourneyOn,
  coords,
  disableFields,
}: {
  field: FileField;
  dispatch: Function;
  selectedFile: RequestFile;
  currentReviewer: RequestParticipant;
  currentFieldOnFocus: number;
  setCurrentFieldOnFocus: Function;
  moveToFieldLocation: Function;
  signingJourneyOn: boolean;
  coords?: any;
  disableFields?: boolean;
}) => {
  const theme = useTheme();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string>("");
  const [menuOpen, setMenuOpen] = useState(false);
  const signerColor = fieldColorFor(field.signerIndex);
  const isReviewer = isTruthy(currentReviewer?.email);
  // `disableFields` is true in the interactive signing view (same convention
  // as CustomTextFieldsElement).
  const interactive = !!disableFields && !isReviewer && !field.readOnly;
  const options = (field.listItems ?? []).filter(
    (item) => item.text.trim() !== "",
  );
  const value = field.value ?? "";
  const isFocused = currentFieldOnFocus === field.id;

  useEffect(() => {
    if (isFocused) {
      triggerRef.current?.focus();
    }
    setError(customFieldValueError(field, value));
  }, [currentFieldOnFocus]);

  const handleSelect = (next: string) => {
    setMenuOpen(false);
    setError(customFieldValueError(field, next));
    dispatch({
      type: "UPDATE_FIELD",
      fileId: selectedFile.unsignedPdf || selectedFile.unsignedDoc,
      fieldId: field.name,
      value: next,
    });
    emitFieldCommit();
    if (next && signingJourneyOn) {
      moveToFieldLocation(field.id + 1, true);
    }
  };

  // Same scaling as CustomTextFieldsElement so the two line up on a page.
  const pageDims = selectedFile?.dimensions?.[field.page];
  const ry = pageDims?.y ? coords.height / pageDims.y : 1;
  const styleSize = field.styles?.size ?? 16;
  const fontSize =
    Math.min(coords.pageHeight - 1, styleSize * ry) || styleSize * ry;

  return (
    <Tooltip
      describeChild
      title={interactive ? field.toolTip || "" : ""}
      placement="top"
      arrow
    >
      <Box
        sx={{
          position: "relative",
          height: coords.pageHeight - 2,
          width: isTruthy(coords) ? coords.pageWidth : field.w,
          borderRadius: "6px",
          backgroundColor: !interactive
            ? field.required
              ? alpha(signerColor, 0.09)
              : "transparent"
            : field.required
              ? alpha(theme.palette.error.main, 0.12)
              : "transparent",
          border: interactive
            ? error
              ? `1.5px dashed ${theme.palette.error.main}`
              : isFocused
                ? `1.5px solid ${theme.palette.error.main}`
                : `1.5px dashed ${signerColor}`
            : `1px solid ${signerColor}`,
          boxShadow:
            interactive && isFocused && !error
              ? `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`
              : "none",
          transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        }}
      >
        <Box
          component="button"
          type="button"
          ref={triggerRef}
          id={`readOnly-input-field-${field.id}`}
          disabled={!interactive}
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-label={field.placeholder || field.label || "Drop down"}
          aria-required={field.required}
          aria-invalid={!!error}
          aria-describedby={
            error && interactive ? `field-error-${field.id}` : undefined
          }
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setMenuOpen(true);
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setMenuOpen(true);
            }
          }}
          onFocus={(e: React.FocusEvent) => {
            e.stopPropagation();
            setCurrentFieldOnFocus(field.id);
          }}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            margin: 0,
            padding: "0 2px",
            display: "flex",
            alignItems: "center",
            gap: "2px",
            border: "none",
            outline: "none",
            background: "transparent",
            boxSizing: "border-box",
            textAlign: "left",
            fontSize: fontSize > 0 ? fontSize : undefined,
            fontFamily: field.styles?.fontFamily,
            color: value ? field.styles?.color : theme.palette.text.secondary,
            fontWeight: field.styles?.fontWeight?.bold ? "bold" : "normal",
            fontStyle: field.styles?.fontWeight?.italic ? "italic" : "normal",
            textDecoration: field.styles?.fontWeight?.underline
              ? "underline"
              : "none",
            cursor: interactive ? "pointer" : "default",
          }}
        >
          <Box
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value || field.placeholder || "Select an option"}
          </Box>
          {interactive && (
            <KeyboardArrowDownRounded
              sx={{
                flexShrink: 0,
                fontSize: "1.1em",
                color: "text.secondary",
                transition: "transform 0.15s ease",
                transform: menuOpen ? "rotate(180deg)" : "none",
              }}
            />
          )}
        </Box>
        {/* Card, item rhythm, hover and selected states come from the global
            MuiMenu / MuiMenuItem overrides, so this matches the kebab menus. */}
        <Menu
          anchorEl={triggerRef.current}
          open={menuOpen && interactive}
          onClose={() => setMenuOpen(false)}
          // Clicks inside the portal still bubble through React to the page.
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          transformOrigin={{ horizontal: "left", vertical: "top" }}
          // The journey moves focus to the next field after a pick.
          disableRestoreFocus={signingJourneyOn}
          PaperProps={{
            sx: {
              mt: "4px",
              // At least as wide as the field, but never squeezed to a tiny
              // field's width on the scaled-down mobile page.
              minWidth: Math.max(
                triggerRef.current?.offsetWidth ?? 0,
                menu.minWidth,
              ),
              maxWidth: "calc(100vw - 32px)",
              maxHeight: 320,
            },
          }}
          MenuListProps={{
            role: "listbox",
            "aria-label": field.placeholder || field.label || "Drop down",
          }}
        >
          {!field.required && value && (
            <MenuItem
              role="option"
              aria-selected={false}
              onClick={() => handleSelect("")}
              sx={{ color: "text.secondary" }}
            >
              {field.placeholder || "Select an option"}
            </MenuItem>
          )}
          {options.map((item) => (
            <MenuItem
              key={item.id}
              role="option"
              aria-selected={item.text === value}
              selected={item.text === value}
              onClick={() => handleSelect(item.text)}
              // Long options wrap instead of being cut off on a phone.
              sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
            >
              {item.text}
            </MenuItem>
          ))}
        </Menu>
        {error && interactive && (
          <Box
            id={`field-error-${field.id}`}
            role="alert"
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              color: "error.main",
              fontSize: "10px",
              fontWeight: 500,
              whiteSpace: "nowrap",
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              padding: "1px 4px",
              zIndex: 1000,
              pointerEvents: "none",
              borderRadius: "2px",
              border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
              lineHeight: 1.4,
            }}
          >
            {error}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default CustomDropDownFieldElement;
